import Entity from "./entity";
import Option from "./option";

/**
 * Order in a given date with the information specific to that moment.
 *
 * Object representation for a Order document from the database.
 */
class Order extends Entity {
  constructor({
    id,
    restaurantId,
    date,
    hasBeenPaid,
    products,
    location,
    session,
    userId
  } = {}) {
    super({ id: id, restaurantId: restaurantId });
    this.date = date;
    this.products = products;
    this.hasBeenPaid = hasBeenPaid;
    this.location = location;
    this.userId = userId;
    this.session = session;
  }

  /**
   * @returns A plain JS object containing the fields as they will be saved to the
   * database.
   */
  _toMap() {
    return {
      id: this.id,
      date: this.date,
      type: this.type,
      data: this.data,
      hasBeenPaid: this.hasBeenPaid,
      location: this.location,
      userId: this.userId,
      session: this.session,
      products: this.products
        ? this.products.map(product => product.toMap())
        : []
    };
  }

  /**
   * @param {*} map `Order` document data as a plain JS object
   * snapshot represented by this `Order`.
   *
   * @returns {Order} A new instance created from the provided map.
   */
  static _fromMap(map) {
    if (map.products) {
      // An Order should always have at least one product
      map.products = map.products.map(OrderProduct.fromMap);
    }
    map.date = map.date
      ? map.date.toDate
        ? map.date.toDate()
        : map.date
      : null;

    let order = new Order(map);
    return order;
  }

  /**
   * @returns {string} A `string` representation of the collection to which this
   * `Order` belongs to.
   */
  get _functionUrl() {
    return "orders";
  }

  get _entityName() {
    return "order";
  }

  get _collectionName() {
    return "orders";
  }

  updateProductsStatus(from, to, selectedWrappers) {
    if (!selectedWrappers) {
      this.products.forEach(product => {
        if (from === product.status) {
          product.status = to;
        }
      });
    } else {
      selectedWrappers.forEach(wrapper => {
        this.products[wrapper.index].status = to;
      });
    }
    this.save();
  }
}

const OrderProductStatus = Object.freeze({
  pending: "pending",
  ready: "ready",
  preparing: "preparing",
  delivered: "delivered",
  next: status => {
    switch (status) {
      case "pending":
        return "preparing";
      case "preparing":
        return "ready";
      case "ready":
        return "delivered";
      case "delivered":
        return null;
    }
  },
  previous: status => {
    switch (status) {
      case "pending":
        return null;
      case "preparing":
        return "pending";
      case "ready":
        return "preparing";
      case "delivered":
        return "ready";
    }
  }
});

/**
 * Represents a `Product` that belongs to an `Order` in a specific moment.
 */
class OrderProduct {
  constructor({
    id,
    comment,
    name,
    image,
    price,
    category,
    options,
    status
  } = {}) {
    this.id = id;
    this.comment = comment;
    this.name = name;
    this.image = image;
    this.price = price;
    this.category = category;
    this.options = options;
    this.status = status;
  }

  toMap() {
    return {
      id: this.id,
      comment: this.comment,
      name: this.name,
      image: this.image,
      price: this.price,
      category: this.category,
      options: this.options ? this.options.map(option => option.toMap()) : [],
      status: this.status
    };
  }

  static fromMap(product) {
    product.options = product.options
      ? product.options.map(Option.fromMap)
      : [];
    return new OrderProduct(product);
  }
}

export { Order, OrderProduct, OrderProductStatus };
